import useRequest from "@/utils/useReq";
import { useCallback, useMemo, useState } from "react";
import { Case, Switch } from "react-if";
import { getCertList, getUserRight, updateCert } from "../../../services";
import AuthSubject from "../../AuthSubject/form";
import HBMessage from "../../HBMessage";
import HBModal from "../../HBModal";
import PayPage from "../pay";
import PayResult from "../payResult";
import VipHeader from "../shared/header";
import {
  getVipExpireTime,
  getVipPkgExpireTime,
  getVipTemExpireTime,
  getVipUpgradeExpireTime,
  isAuthPkg,
  isTemPkg,
  isUpgradePkg,
} from "../shared/utils";
import SubjectConfirm from "../subjectConfirm";
import styles from "./index.module.less";

import dayjs from "dayjs";

import { getVipTypeById } from "@/constants/vip";
import useAppStore from "@/context/store";
import formatTime from "@/utils/formatTime";

const ft = (date) => formatTime(date, "YYYY/MM/DD");

/**
 * 订单继续支付弹框
 * @typedef {(step: 'overview'|'pay'|'choice')=>any} Cancel
 * @param {Object} props
 * @param {Object} props.userRight 会员权益信息（可传可不传）
 * @param {(order: Object)=>any} props.onFinish 流程结束，且订单已完成， 授权主体有没有填写未知
 * @param {(step: 'overview'|'pay'|'choice' )=> any} props.onAborted 订单未支付,流程中止
 * @param {(order: Object)=>any} props.onPaid 订单完成，但流程还在继续
 * @param {function} props.onOK 所有流程完成,且 且弹框关闭
 * @param {Cancel} props.onCancel 所有流程完成,且弹框关闭 参数 step 当前页面的位置
 */
export default function OrderPay({
  userRight,
  order,
  onOk,
  onCancel,
  onPaid,
  onFinish,
  onAborted,
  ...props
}) {
  // 权益信息
  const { loading: rightLoading, data: rightData } = useRequest(async () => {
    if (userRight) return userRight;
    else return getUserRight();
  });
  const { user, refreshUser } = useAppStore(({ user, refreshUser }) => ({
    user,
    refreshUser,
  }));
  // 步骤
  const [step, setStep] = useState("pay");
  const [subject, setSubject] = useState({});

  // 处理详情信息
  const details = useMemo(() => {
    if (rightLoading) return [];

    const isVipUpgrade = order.trade_mode === "promotion";
    const { vips, auth_rights, upgrade_rights } = rightData;

    const list = order.skus.map((sku) => {
      let expireTime;
      if (isVipUpgrade) {
        expireTime = ft(getVipUpgradeExpireTime(vips, sku.ref_body));
      } else {
        if (isAuthPkg(sku.ref_body)) {
          expireTime = ft(
            getVipPkgExpireTime(sku.ref_body, auth_rights, sku.quantity)
          );
        } else if (isTemPkg(sku.ref_body)) {
          expireTime = formatTime(
            getVipTemExpireTime(sku.ref_body),
            "YYYY/MM/DD HH:mm:ss"
          );
        } else if (isUpgradePkg(sku.ref_body)) {
          expireTime = ft(
            getVipPkgExpireTime(sku.ref_body, upgrade_rights, sku.quantity)
          );
        } else {
          expireTime = ft(getVipExpireTime(vips, sku.ref_body));
        }
      }

      return {
        price: sku.price,
        name: sku.ref_body.name,
        number: sku.quantity,
        goods: sku.ref_body.good,
        expireTime,
        years:
          isVipUpgrade && dayjs(expireTime).diff(dayjs(), "y", true).toFixed(2),
      };
    });

    // 个人会员抵扣、优惠券
    order.promotions.forEach((item) => {
      let name = "优惠券";
      if (item.type === "activity.plus_buy") {
        name = "换购活动";
      } else if (item.type === "trade.promotion") {
        name = "个人VIP升级抵扣";
      }

      list.push({
        type: item.type,
        name,
        price: -item.amount,
        number: 1,
      });
    });

    return list;
  }, [order, rightData, rightLoading]);

  const orderVipType = useMemo(() => {
    const goods = order.skus[0].ref_body.goods;
    // 判断第一个sku是否是会员商品
    return goods.type === "vip" && getVipTypeById(goods.vip_id);
  }, [order]);

  const next = useCallback(
    (params) => {
      if (step === "pay") setStep("payResult");
      if (step === "payResult") setStep("authSubject");
      if (step === "authSubject") {
        setSubject(params);
        setStep("subjectConfirm");
      }
    },
    [step]
  );

  const onOrderDone = useCallback(
    async (order) => {
      // setOrder(order);
      next(order);
      onPaid && onPaid(order);
      refreshUser();
    },
    [next, onPaid, refreshUser]
  );

  const onSubmit = useCallback(async () => {
    const certSubject = await getCertList({
      source_no: order.order_no,
    });

    if (!certSubject?.list?.length > 0) {
      HBMessage.error("获取授权信息失败");
      return;
    }

    const result = await updateCert({
      id: certSubject.list[0].id,
      subject,
    });

    if (result.err) return;

    HBMessage.success("提交成功");
    onOk && onOk();
  }, [onOk, order, subject]);

  // 取消弹框
  const onCancelWrap = useCallback(() => {
    onCancel && onCancel(step);
    // 不在前两步，调用 onFinish
    if (step !== "pay") {
      onFinish && onFinish(order);
    } else {
      // 支付终止
      onAborted && onAborted(step);
    }
  }, [onCancel, onFinish, order, step, onAborted]);

  if (rightLoading) {
    return null;
  }

  return (
    <HBModal
      className={styles.dialog}
      {...props}
      width={960}
      footer={null}
      bodyStyle={{ padding: 0, overflowX: "auto" }}
      onCancel={onCancelWrap}
    >
      <div className={styles.container}>
        {["pay"].includes(step) && (
          <VipHeader
            user={user}
            userRight={rightData}
            hasBack={false}
            onCancel={onCancelWrap}
          />
        )}
        <div
          className={styles.scrollWarp}
          style={{
            height: ["pay"].includes(step) ? "calc(100% - 105px)" : "100%",
          }}
        >
          <Switch>
            <Case condition={step === "pay"}>
              <PayPage
                details={details}
                order={order}
                user={user}
                onOrderDone={onOrderDone}
              />
            </Case>
            <Case condition={step === "payResult"}>
              <PayResult
                title={orderVipType ? "恭喜你加入花瓣素材VIP" : "购买成功"}
                onNext={next}
                order={order}
                show={!!orderVipType}
              />
            </Case>
            <Case condition={step === "authSubject"}>
              <div className={styles.subjectWrap}>
                <h2>填写被授权方</h2>
                <AuthSubject
                  type={orderVipType}
                  initValue={subject}
                  scene="buyVip"
                  onSubmit={next}
                  onCancel={onCancelWrap}
                />
              </div>
            </Case>
            <Case condition={step === "subjectConfirm"}>
              <SubjectConfirm
                subject={subject}
                onBack={() => {
                  setStep("authSubject");
                }}
                onOk={onSubmit}
              />
            </Case>
          </Switch>
        </div>
      </div>
    </HBModal>
  );
}
